import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { organizationsApi } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { Organization } from '@/types';
import { ClipboardIcon, ArrowPathIcon, BuildingLibraryIcon, PlusCircleIcon } from '@heroicons/react/24/outline';

export default function OrgSettings() {
  const { currentOrganization, setCurrentOrganization } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isCheckingStripe, setIsCheckingStripe] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);

  const [settings, setSettings] = useState({
    name: currentOrganization?.name || '',
    website: currentOrganization?.website || '',
    defaultCommissionRate: currentOrganization?.settings?.defaultCommissionRate || 10,
    defaultCookieDuration: currentOrganization?.settings?.defaultCookieDuration || 30,
    defaultHoldPeriod: currentOrganization?.settings?.defaultHoldPeriod || 14,
    autoApproveAffiliates: currentOrganization?.settings?.autoApproveAffiliates || false,
    listInDirectory: currentOrganization?.settings?.listInDirectory || false,
    minPayoutAmount: (currentOrganization?.settings?.minPayoutAmount || 5000) / 100,
  });

  // Fetch fresh organization data on mount to ensure Stripe status is current
  useEffect(() => {
    const fetchOrganization = async () => {
      if (!currentOrganization?.organizationId) return;
      try {
        const response = await organizationsApi.get(currentOrganization.organizationId);
        if (response.data.success) {
          const org = response.data.data.organization;
          setCurrentOrganization(org as Organization);
          // Update local settings state with fresh data
          setSettings({
            name: org.name || '',
            website: org.website || '',
            defaultCommissionRate: org.settings?.defaultCommissionRate || 10,
            defaultCookieDuration: org.settings?.defaultCookieDuration || 30,
            defaultHoldPeriod: org.settings?.defaultHoldPeriod || 14,
            autoApproveAffiliates: org.settings?.autoApproveAffiliates || false,
            listInDirectory: org.settings?.listInDirectory || false,
            minPayoutAmount: (org.settings?.minPayoutAmount || 5000) / 100,
          });
        }
      } catch (error) {
        console.error('Failed to fetch organization:', error);
      }
    };
    fetchOrganization();
  }, [currentOrganization?.organizationId]);

  // Handle Stripe Connect callback
  useEffect(() => {
    const stripeSuccess = searchParams.get('stripe_success');
    const stripeRefresh = searchParams.get('stripe_refresh');

    if ((stripeSuccess === 'true' || stripeRefresh === 'true') && currentOrganization?.organizationId) {
      const checkStripeStatus = async () => {
        setIsCheckingStripe(true);
        try {
          const response = await organizationsApi.stripeCallback(currentOrganization.organizationId);
          if (response.data.success) {
            const stripeData = response.data.data;
            // Update organization with new Stripe status
            setCurrentOrganization({
              ...currentOrganization,
              stripeAccountId: stripeData.stripeAccountId,
              stripeAccountStatus: stripeData.stripeAccountStatus,
            } as Organization);

            if (stripeData.stripeAccountStatus === 'active') {
              toast.success('Stripe account connected successfully!');
            } else if (stripeSuccess === 'true') {
              toast.success('Stripe account connected. Additional verification may be required.');
            }
          }
        } catch (error) {
          console.error('Failed to check Stripe status:', error);
          if (stripeSuccess === 'true') {
            toast.error('Failed to verify Stripe connection. Please try again.');
          }
        } finally {
          setIsCheckingStripe(false);
          // Clear URL params
          setSearchParams({});
        }
      };

      checkStripeStatus();
    }
  }, [searchParams, currentOrganization?.organizationId]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrganization) return;

    setIsUpdating(true);
    try {
      const response = await organizationsApi.update(currentOrganization.organizationId, {
        name: settings.name,
        website: settings.website,
        settings: {
          ...currentOrganization.settings,
          defaultCommissionRate: settings.defaultCommissionRate,
          defaultCookieDuration: settings.defaultCookieDuration,
          defaultHoldPeriod: settings.defaultHoldPeriod,
          autoApproveAffiliates: settings.autoApproveAffiliates,
          listInDirectory: settings.listInDirectory,
          minPayoutAmount: settings.minPayoutAmount * 100,
        },
      });

      if (response.data.success) {
        setCurrentOrganization(response.data.data.organization as Organization);
        toast.success('Settings saved successfully');
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to save settings');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRegenerateApiKey = async () => {
    if (!currentOrganization) return;
    if (!confirm('Are you sure? This will invalidate your current API key.')) return;

    setIsRegenerating(true);
    try {
      const response = await organizationsApi.regenerateApiKey(currentOrganization.organizationId);
      if (response.data.success) {
        toast.success('API key regenerated');
        // Copy new key to clipboard
        navigator.clipboard.writeText(response.data.data.apiKey);
        toast.success('New API key copied to clipboard');
      }
    } catch {
      toast.error('Failed to regenerate API key');
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleConnectStripeExpress = async () => {
    if (!currentOrganization) return;

    setIsConnecting(true);
    setShowConnectModal(false);
    try {
      const response = await organizationsApi.connectStripe(currentOrganization.organizationId);
      if (response.data.success) {
        window.location.href = response.data.data.url;
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to connect Stripe');
      setIsConnecting(false);
    }
  };

  const handleConnectStripeOAuth = async () => {
    if (!currentOrganization) return;

    setIsConnecting(true);
    setShowConnectModal(false);
    try {
      const response = await organizationsApi.connectStripeOAuth(currentOrganization.organizationId);
      if (response.data.success) {
        window.location.href = response.data.data.url;
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to connect Stripe');
      setIsConnecting(false);
    }
  };

  const handleCopySlug = () => {
    navigator.clipboard.writeText(currentOrganization?.slug || '');
    toast.success('Slug copied');
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-display font-bold text-charcoal">Settings</h2>
        <p className="text-charcoal/60 mt-1">Configure your affiliate program</p>
      </div>

      {/* Organization Info */}
      <div className="rounded-xl bg-warm-white shadow-sm">
        <div className="px-6 py-4 border-b border-tan/30">
          <h3 className="text-lg font-display font-semibold text-charcoal">Organization Info</h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between p-4 bg-cream/50 rounded-lg">
            <div>
              <p className="text-sm text-charcoal/60">Organization Slug</p>
              <code className="text-lg text-rust">{currentOrganization?.slug}</code>
            </div>
            <button onClick={handleCopySlug} className="btn btn-secondary">
              <ClipboardIcon className="w-4 h-4 mr-2" />
              Copy
            </button>
          </div>
        </div>
      </div>

      {/* Stripe Connect */}
      <div className="rounded-xl bg-warm-white shadow-sm">
        <div className="px-6 py-4 border-b border-tan/30">
          <h3 className="text-lg font-display font-semibold text-charcoal">Stripe Connect</h3>
        </div>
        <div className="p-6">
          {isCheckingStripe ? (
            <div className="flex items-center gap-4 p-4 bg-tan/10 rounded-lg border border-tan/30">
              <div className="w-10 h-10 bg-tan rounded-full flex items-center justify-center animate-pulse">
                <ArrowPathIcon className="w-5 h-5 text-charcoal animate-spin" />
              </div>
              <div>
                <p className="font-medium text-charcoal">Verifying Stripe Connection...</p>
                <p className="text-sm text-charcoal/60">Please wait while we verify your account status</p>
              </div>
            </div>
          ) : currentOrganization?.stripeAccountStatus === 'active' || currentOrganization?.stripeAccountId ? (
            <div className="flex items-center gap-4 p-4 bg-sage/10 rounded-lg border border-sage/30">
              <div className="w-10 h-10 bg-sage rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-charcoal">Stripe Connected</p>
                <p className="text-sm text-sage">
                  {currentOrganization?.stripeAccountStatus === 'active'
                    ? 'Your account is ready to process payouts'
                    : 'Your account is connected but may need additional setup in Stripe'}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-charcoal/60">
                Connect your Stripe account to process affiliate payouts.
              </p>
              <button
                onClick={() => setShowConnectModal(true)}
                disabled={isConnecting}
                className="btn btn-primary"
              >
                {isConnecting ? 'Connecting...' : 'Connect Stripe'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Stripe Connect Choice Modal */}
      {showConnectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-warm-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <h3 className="text-xl font-display font-bold text-charcoal mb-2">
              Connect Stripe Account
            </h3>
            <p className="text-charcoal/60 mb-6">
              Choose how you'd like to connect your Stripe account:
            </p>

            <div className="space-y-4">
              {/* Existing Account Option */}
              <button
                onClick={handleConnectStripeOAuth}
                disabled={isConnecting}
                className="w-full p-4 border-2 border-tan/30 rounded-lg hover:border-rust/50 hover:bg-cream/30 transition-all text-left group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-sage/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-sage/30">
                    <BuildingLibraryIcon className="w-5 h-5 text-sage" />
                  </div>
                  <div>
                    <p className="font-semibold text-charcoal">I have an existing Stripe account</p>
                    <p className="text-sm text-charcoal/60 mt-1">
                      Connect your existing Stripe account. You'll keep full access to your Stripe dashboard.
                    </p>
                  </div>
                </div>
              </button>

              {/* New Account Option */}
              <button
                onClick={handleConnectStripeExpress}
                disabled={isConnecting}
                className="w-full p-4 border-2 border-tan/30 rounded-lg hover:border-rust/50 hover:bg-cream/30 transition-all text-left group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-rust/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-rust/30">
                    <PlusCircleIcon className="w-5 h-5 text-rust" />
                  </div>
                  <div>
                    <p className="font-semibold text-charcoal">I need to create a new Stripe account</p>
                    <p className="text-sm text-charcoal/60 mt-1">
                      We'll guide you through creating a new Stripe account for your affiliate program.
                    </p>
                  </div>
                </div>
              </button>
            </div>

            <button
              onClick={() => setShowConnectModal(false)}
              className="w-full mt-4 py-2 text-charcoal/60 hover:text-charcoal transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* API Key */}
      <div className="rounded-xl bg-warm-white shadow-sm">
        <div className="px-6 py-4 border-b border-tan/30">
          <h3 className="text-lg font-display font-semibold text-charcoal">API Key</h3>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-charcoal/60">
            Use this API key to record sales and conversions from your backend.
          </p>
          <button onClick={handleRegenerateApiKey} disabled={isRegenerating} className="btn btn-secondary">
            <ArrowPathIcon className="w-4 h-4 mr-2" />
            {isRegenerating ? 'Regenerating...' : 'Regenerate API Key'}
          </button>
        </div>
      </div>

      {/* Program Settings */}
      <div className="rounded-xl bg-warm-white shadow-sm">
        <div className="px-6 py-4 border-b border-tan/30">
          <h3 className="text-lg font-display font-semibold text-charcoal">Program Settings</h3>
        </div>
        <form onSubmit={handleSave} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-charcoal mb-1">Organization Name</label>
              <input
                type="text"
                value={settings.name}
                onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-charcoal mb-1">Website</label>
              <input
                type="url"
                value={settings.website}
                onChange={(e) => setSettings({ ...settings, website: e.target.value })}
                className="input"
                placeholder="https://yoursite.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-charcoal mb-1">Default Commission Rate (%)</label>
              <input
                type="number"
                value={settings.defaultCommissionRate}
                onChange={(e) => setSettings({ ...settings, defaultCommissionRate: Number(e.target.value) })}
                className="input"
                min="0"
                max="100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-charcoal mb-1">Cookie Duration (days)</label>
              <input
                type="number"
                value={settings.defaultCookieDuration}
                onChange={(e) => setSettings({ ...settings, defaultCookieDuration: Number(e.target.value) })}
                className="input"
                min="1"
                max="365"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-charcoal mb-1">Commission Hold Period (days)</label>
              <input
                type="number"
                value={settings.defaultHoldPeriod}
                onChange={(e) => setSettings({ ...settings, defaultHoldPeriod: Number(e.target.value) })}
                className="input"
                min="0"
                max="90"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-charcoal mb-1">Minimum Payout Amount ($)</label>
              <input
                type="number"
                value={settings.minPayoutAmount}
                onChange={(e) => setSettings({ ...settings, minPayoutAmount: Number(e.target.value) })}
                className="input"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="autoApprove"
                checked={settings.autoApproveAffiliates}
                onChange={(e) => setSettings({ ...settings, autoApproveAffiliates: e.target.checked })}
                className="rounded border-tan bg-warm-white text-rust focus:ring-rust"
              />
              <label htmlFor="autoApprove" className="text-charcoal/80">
                Auto-approve new affiliate applications
              </label>
            </div>

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="listInDirectory"
                checked={settings.listInDirectory}
                onChange={(e) => setSettings({ ...settings, listInDirectory: e.target.checked })}
                className="rounded border-tan bg-warm-white text-rust focus:ring-rust mt-0.5"
              />
              <div>
                <label htmlFor="listInDirectory" className="text-charcoal/80 block">
                  List program in affiliate directory
                </label>
                <p className="text-xs text-charcoal/50 mt-0.5">
                  Allow affiliates to discover and apply to your program through the public directory
                </p>
              </div>
            </div>
          </div>

          <button type="submit" disabled={isUpdating} className="btn btn-primary">
            {isUpdating ? 'Saving...' : 'Save Settings'}
          </button>
        </form>
      </div>
    </div>
  );
}
